import { Shift } from '../../shifts/models/shift'

/*
|--------------------------------------------------------------------------
| Shift maths
|--------------------------------------------------------------------------
|
| Pure functions over a shift. The API reports durations as Luxon Durations
| and money in minor units (cents); everything here keeps those units so the
| formatters stay the only place that converts.
*/

export interface ShiftTotals {
  /** Worked minutes */
  minutes: number
  /** Cost in the shift's minor currency unit */
  cost: number
}

export const ZERO_TOTALS: Readonly<ShiftTotals> = Object.freeze({
  minutes: 0,
  cost: 0,
})

/** Minutes from start to end, preferring the server's figure when it has one. */
export function shiftMinutes(shift: Shift): number {
  const reported = shift.totalDurationMinutes?.as('minutes')
  if (reported !== undefined && Number.isFinite(reported)) {
    return reported
  }

  const span = shift.endDate.diff(shift.startDate).as('minutes')

  return span > 0 ? span : 0
}

/** Minutes that are paid: the server's figure, else the whole shift. */
export function payableMinutes(shift: Shift): number {
  const reported = shift.payableDurationMinutes?.as('minutes')
  if (reported !== undefined && Number.isFinite(reported)) {
    return reported
  }

  return shiftMinutes(shift)
}

/** Cost in minor units: the server's total, else the hourly rate over the payable time. */
export function shiftCost(shift: Shift): number {
  if (shift.totalRateAmount != null) {
    return shift.totalRateAmount
  }

  if (!shift.rateAmount) {
    return 0
  }

  return Math.round((shift.rateAmount * payableMinutes(shift)) / 60)
}

export function shiftTotals(shift: Shift): ShiftTotals {
  return { minutes: shiftMinutes(shift), cost: shiftCost(shift) }
}

export function addTotals(a: ShiftTotals, b: ShiftTotals): ShiftTotals {
  return { minutes: a.minutes + b.minutes, cost: a.cost + b.cost }
}

export function sumTotals(shifts: Iterable<Shift>): ShiftTotals {
  let total: ShiftTotals = ZERO_TOTALS
  for (const shift of shifts) {
    total = addTotals(total, shiftTotals(shift))
  }

  return total
}

/** The shift was called off; the calendar draws it only when asked to. */
export function isCancelled(shift: Shift): boolean {
  return shift.status === 'cancelled'
}

/** Someone is rostered on the shift. */
export function isFilled(shift: Shift): boolean {
  return shift.rosterEmployeeId !== null
}

/**
 * The shift can go out to staff. An unpublished shift is publishable when it
 * is either filled or set up to be offered; an empty, un-offered shift would
 * be invisible to everyone once published, so it is held back.
 */
export function isPublishable(shift: Shift): boolean {
  if (shift.published || isCancelled(shift)) {
    return false
  }

  return shift.distribution !== 'none' || isFilled(shift)
}
